// client/src/components/templates/TemplatesPage.js - VERSÃO COMPLETA
import React, { useState, useCallback, useEffect, Fragment } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isMatrizAdmin as checkIsMatrizAdmin } from '../../utils/permissions';
import { useMutateTemplateUserPreference } from '../../hooks/useTemplateUserPreference';
import { PlusCircle, ArrowLeft, SlidersHorizontal, ClipboardList, FileText, MessageSquare, Receipt, Award, XCircle } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';

import AnamneseTemplates from './anamnese/AnamneseTemplates';
import CertificadoTemplates from './certificados/CertificadoTemplates';
import ContratoTemplates from './contratos/ContratoTemplates';
import MensagemTemplates from './mensagens/MensagemTemplates';
import ReciboTemplates from './recibos/ReciboTemplates';

const templateConfig = {
    anamnese: { Component: AnamneseTemplates, title: 'Templates de Anamnese', icon: <ClipboardList /> },
    certificado: { Component: CertificadoTemplates, title: 'Templates de Certificado', icon: <Award /> },
    contrato: { Component: ContratoTemplates, title: 'Templates de Contrato', icon: <FileText /> },
    mensagem: { Component: MensagemTemplates, title: 'Templates de Mensagem', icon: <MessageSquare /> },
    recibo: { Component: ReciboTemplates, title: 'Templates de Recibo', icon: <Receipt /> },
};

const TemplatesPage = () => {
    const { tipo } = useParams();
    const location = useLocation();
    const { user } = useAuth();
    const { updatePreference, isUpdatingPreference } = useMutateTemplateUserPreference();

    const [showForm, setShowForm] = useState(false);
    const [currentTemplateId, setCurrentTemplateId] = useState(null);
    const [showFilters, setShowFilters] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    const isMatrizAdmin = checkIsMatrizAdmin(user) && !user?.account && !user?.franquia;
    const canManage = !!user;
    const { title, icon } = templateConfig[tipo] || {};

    useEffect(() => {
        setShowForm(false);
        setCurrentTemplateId(null);
        setPreviewTemplate(null);
    }, [tipo, location.pathname]);

    const handleAddNew = useCallback(() => {
        setCurrentTemplateId(null);
        setShowForm(true);
        setPreviewTemplate(null);
    }, []);

    const handleCloseForm = useCallback(() => {
        setShowForm(false);
        setCurrentTemplateId(null);
        setRefreshTrigger(t => t + 1);
    }, []);

    const handleEdit = useCallback((id) => {
        setCurrentTemplateId(id);
        setShowForm(true);
        setPreviewTemplate(null);
    }, []);

    const handlePreview = useCallback((template) => {
        setPreviewTemplate(template);
        setShowForm(false);
        setCurrentTemplateId(null);
    }, []);

    const handleClosePreview = useCallback(() => {
        setPreviewTemplate(null);
    }, []);

    const handlePreferenceChange = useCallback((template, newStatus) => {
        updatePreference.mutate({
            templateId: template._id,
            tipoTemplate: tipo,
            ativoParaEscopo: newStatus
        });
    }, [updatePreference, tipo]);

    const CurrentTemplateComponent = templateConfig[tipo]?.Component;

    if (!CurrentTemplateComponent) {
        return <div className="p-8 text-center text-red-600">Tipo de template inválido: {tipo}</div>;
    }

    const isInPreview = !!previewTemplate;
    const isInForm = showForm && !previewTemplate;
    const isInList = !showForm && !previewTemplate;

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={icon}
                    title={isInPreview ? `Visualizando: ${previewTemplate?.nome || 'Template'}` : title}
                    actions={<>
                        {isInList && (
                            <button
                                onClick={() => setShowFilters(f => !f)}
                                className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                            >
                                <SlidersHorizontal size={18} className="icon"/>
                                {showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}
                            </button>
                        )}
                        {isInForm && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon"/>
                                Fechar
                            </button>
                        )}
                        {isInPreview && (
                            <button onClick={handleClosePreview} className="action-button action-button-secondary">
                                <ArrowLeft size={18} className="icon"/>
                                Voltar para Lista
                            </button>
                        )}
                    </>}
                />

                <CurrentTemplateComponent
                    canManage={canManage}
                    isMatrizAdmin={isMatrizAdmin}
                    showFormGlobal={showForm}
                    currentTemplateIdGlobal={currentTemplateId}
                    showFiltersGlobal={showFilters}
                    refreshTriggerGlobal={refreshTrigger}
                    onEditGlobal={handleEdit}
                    onCloseFormGlobal={handleCloseForm}
                    onRefreshGlobal={() => setRefreshTrigger(t => t + 1)}
                    onPreferenceChangeGlobal={handlePreferenceChange}
                    isUpdatingPreferenceGlobal={isUpdatingPreference}
                    currentTipo={tipo}
                    previewTemplateGlobal={previewTemplate}
                    onPreviewGlobal={handlePreview}
                    onClosePreviewGlobal={handleClosePreview}
                />
            </div>

            <FabButton
                onClick={handleAddNew}
                title="Adicionar Template"
                visible={isInList && canManage}
                icon={<FileText size={26} className="icon"/>}
            />
        </Fragment>
    );
};

export default TemplatesPage;
